import React, { useState, useCallback } from 'react';
import useDictation, { dictationSupported } from '../../platform/engine/useDictation';
import { requestEstimateDraft } from '../../platform/engine/aiEstimateDraft';
import { CATEGORY_OPTIONS } from './roConstants';
import './VoiceEstimateModal.css';

// Talk-it-out: a tech describes what they found, the draft comes back as
// editable estimate lines, and nothing is written until a human presses Add.
// The review step is not optional — this feeds an invoice.

export default function VoiceEstimateModal({ truck, onClose, onApply }) {
  const [stage, setStage]   = useState('capture'); // capture | review
  const [text, setText]     = useState('');
  const [draft, setDraft]   = useState(null);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);

  const appendFinal = useCallback(chunk => {
    setText(t => (t ? `${t} ${chunk}` : chunk));
  }, []);

  const { listening, interim, error: micError, toggle, stop } = useDictation({ onResult: appendFinal });
  const supported = dictationSupported();

  async function handleDraft() {
    const transcript = text.trim();
    if (!transcript) return;
    stop();
    setBusy(true);
    setError(null);
    try {
      const result = await requestEstimateDraft({
        transcript,
        complaint: truck?.reason || '',
        unit: {
          year:       truck?.year,
          make:       truck?.make,
          model:      truck?.model,
          unitNumber: truck?.unitNumber,
        },
      });
      setDraft(result);
      setStage('review');
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  }

  function patchDraft(field, value) {
    setDraft(d => ({ ...d, [field]: value }));
  }

  function patchLabor(idx, field, value) {
    setDraft(d => ({
      ...d,
      laborLines: d.laborLines.map((l, i) => i === idx ? { ...l, [field]: value } : l),
    }));
  }

  function patchPart(idx, field, value) {
    setDraft(d => ({
      ...d,
      parts: d.parts.map((p, i) => i === idx ? { ...p, [field]: value } : p),
    }));
  }

  function dropLabor(idx) {
    setDraft(d => ({ ...d, laborLines: d.laborLines.filter((_, i) => i !== idx) }));
  }

  function dropPart(idx) {
    setDraft(d => ({ ...d, parts: d.parts.filter((_, i) => i !== idx) }));
  }

  async function handleApply() {
    setBusy(true);
    try {
      await onApply({
        ...draft,
        laborLines: draft.laborLines
          .map(l => ({ ...l, hours: parseFloat(l.hours) || 0 }))
          .filter(l => l.description.trim() && l.hours > 0),
        parts: draft.parts
          .map(p => ({ ...p, quantity: parseFloat(p.quantity) || 1 }))
          .filter(p => p.description.trim()),
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save.');
      setBusy(false);
    }
  }

  const totalHours = draft
    ? draft.laborLines.reduce((s, l) => s + (parseFloat(l.hours) || 0), 0)
    : 0;

  return (
    <div className="vem-backdrop" onClick={onClose}>
      <div className="vem" onClick={e => e.stopPropagation()}>
        <div className="vem-head">
          <h2>{stage === 'capture' ? 'Talk it out' : 'Review before adding'}</h2>
          <button className="vem-x" type="button" onClick={onClose}>✕</button>
        </div>

        {stage === 'capture' && (
          <div className="vem-body">
            <p className="vem-hint">
              Say what you found and what it needs. Plain shop talk is fine —
              &ldquo;left front brake chamber&rsquo;s leaking, needs a chamber and a slack
              adjuster, about two and a half hours.&rdquo;
            </p>

            {supported ? (
              <button
                type="button"
                className={`vem-mic${listening ? ' vem-mic--live' : ''}`}
                onClick={toggle}
              >
                {listening ? '⏹ Stop' : '🎤 Start talking'}
              </button>
            ) : (
              <p className="vem-warn">
                This browser can&rsquo;t do voice input. Type the finding below instead
                (Chrome, Edge, and Safari support the mic).
              </p>
            )}

            {micError === 'denied' && (
              <p className="vem-warn">Microphone permission was denied. Type it below, or allow the mic and try again.</p>
            )}
            {micError === 'failed' && (
              <p className="vem-warn">Voice input dropped out — likely no signal. Type it below.</p>
            )}

            <textarea
              className="vem-text"
              rows={6}
              value={text + (interim ? ` ${interim}` : '')}
              onChange={e => setText(e.target.value)}
              placeholder="What did you find?"
            />

            {error && <p className="vem-error">{error}</p>}

            <div className="vem-actions">
              <button type="button" className="vem-btn" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="vem-btn vem-btn--go"
                onClick={handleDraft}
                disabled={busy || !text.trim()}
              >
                {busy ? 'Drafting…' : 'Draft the estimate →'}
              </button>
            </div>
          </div>
        )}

        {stage === 'review' && draft && (
          <div className="vem-body">
            <p className="vem-hint">
              Nothing is saved until you press Add. Edit anything that&rsquo;s wrong.
            </p>

            <label className="vem-field">
              <span>Complaint</span>
              <textarea rows={2} value={draft.complaint} onChange={e => patchDraft('complaint', e.target.value)} />
            </label>
            <label className="vem-field">
              <span>Cause</span>
              <textarea rows={2} value={draft.cause} onChange={e => patchDraft('cause', e.target.value)} />
            </label>
            <label className="vem-field">
              <span>Correction</span>
              <textarea rows={2} value={draft.correction} onChange={e => patchDraft('correction', e.target.value)} />
            </label>
            <label className="vem-field">
              <span>Category</span>
              <select value={draft.category || ''} onChange={e => patchDraft('category', e.target.value || null)}>
                <option value="">No category</option>
                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>

            <div className="vem-section">
              <div className="vem-section-head">
                <span>Labor</span>
                <span className="vem-total">{totalHours.toFixed(2)} hrs</span>
              </div>
              {draft.laborLines.length === 0 && <div className="vem-empty">No labor lines.</div>}
              {draft.laborLines.map((l, i) => (
                <div className="vem-row" key={i}>
                  <input
                    className="vem-row-desc"
                    value={l.description}
                    onChange={e => patchLabor(i, 'description', e.target.value)}
                  />
                  <input
                    className="vem-row-num"
                    type="number"
                    step="0.25"
                    min="0"
                    value={l.hours}
                    onChange={e => patchLabor(i, 'hours', e.target.value)}
                  />
                  <span className="vem-row-unit">hrs</span>
                  <button type="button" className="vem-row-x" onClick={() => dropLabor(i)}>✕</button>
                </div>
              ))}
            </div>

            <div className="vem-section">
              <div className="vem-section-head">
                <span>Parts</span>
                <span className="vem-total">price set later</span>
              </div>
              {draft.parts.length === 0 && <div className="vem-empty">No parts.</div>}
              {draft.parts.map((p, i) => (
                <div className="vem-row" key={i}>
                  <input
                    className="vem-row-desc"
                    value={p.description}
                    onChange={e => patchPart(i, 'description', e.target.value)}
                  />
                  <input
                    className="vem-row-num"
                    type="number"
                    step="1"
                    min="1"
                    value={p.quantity}
                    onChange={e => patchPart(i, 'quantity', e.target.value)}
                  />
                  <span className="vem-row-unit">qty</span>
                  <button type="button" className="vem-row-x" onClick={() => dropPart(i)}>✕</button>
                </div>
              ))}
            </div>

            {draft.assumptions.length > 0 && (
              <div className="vem-assume">
                <strong>Assumed, not heard:</strong>
                <ul>{draft.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
              </div>
            )}

            {error && <p className="vem-error">{error}</p>}

            <div className="vem-actions">
              <button type="button" className="vem-btn" onClick={() => setStage('capture')}>← Back</button>
              <button
                type="button"
                className="vem-btn vem-btn--go"
                onClick={handleApply}
                disabled={busy}
              >
                {busy ? 'Adding…' : 'Add to this RO'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
